import styled from 'styled-components';
import { useEffect, useMemo, useState, useCallback } from 'react';
import SearchBar from './board/SearchBar';
import ItemListItem from './board/ItemListItem';
import ImageListItem from './board/ImageListItem';
import PageNav from './PageNav';

const ItemList = ({ items, order }) => {
  const offset = 10; // 페이지당 게시물 수

  const notices = useMemo(
    () => items.filter((it) => it.isPublic === true),
    [items]
  );
  const normals = useMemo(() => items.filter((it) => !it.isPublic), [items]);

  const [isSearch, setIsSearch] = useState(false);
  const [result, setResult] = useState(items);
  const [currentPage, setCurrentPage] = useState(1);

  const totalLength = useMemo(
    () => (isSearch ? result.length : items.length),
    [isSearch, result.length, items.length]
  );

  const pinnedCount = useMemo(
    () => (isSearch ? result.filter((v) => v.isPublic).length : notices.length),
    [isSearch, result, notices.length]
  );

  const pageCount = useMemo(() => {
    const pageBody = Math.max(offset - pinnedCount, 1);
    const normalLen = (isSearch ? result : items).filter(
      (v) => !v.isPublic
    ).length;
    return Math.max(Math.ceil(normalLen / pageBody), 1);
  }, [items, result, isSearch, pinnedCount]);

  const runSearch = useCallback(
    (search, mode) => {
      if (!search) {
        setIsSearch(false);
        setResult(items);
        setCurrentPage(1);
        return;
      }

      const base = [...notices]; // 공지 항상 포함
      const filtered = normals.filter((it) => {
        const t = it.title ?? '';
        const s = it.summary ?? '';
        if (mode === 1) return t.includes(search) || s.includes(search);
        if (mode === 2) return t.includes(search);
        if (mode === 3) return s.includes(search);
        return false;
      });

      setIsSearch(true);
      setResult([...base, ...filtered]);
      setCurrentPage(1);
    },
    [items, notices, normals]
  );

  useEffect(() => {
    if (!isSearch) {
      setResult(items);
    } else {
      const idSet = new Set(items.map((v) => v.id));
      setResult((prev) => prev.filter((v) => idSet.has(v.id)));
    }
  }, [items]); // eslint-disable-line

  const pageItems = useMemo(() => {
    const source = isSearch ? result : items;
    const pin = source.filter((v) => v.isPublic);
    const normal = source.filter((v) => !v.isPublic);

    const bodySize = Math.max(offset - pin.length, 1);
    if (currentPage === 1) {
      return [...pin, ...normal.slice(0, bodySize)];
    }

    const start = bodySize * (currentPage - 1);
    const end = start + bodySize;
    return [...pin, ...normal.slice(start, end)];
  }, [isSearch, result, items, offset, currentPage]);

  return (
    <Wrapper>
      <TopBar>
        <SearchBar onSearch={runSearch} />
        <StateText>
          전체 게시물 {totalLength}개, 현재 {currentPage}페이지 / 전체{' '}
          {pageCount}페이지
        </StateText>
      </TopBar>

      {order !== '/photo/' ? (
        <>
          <HeaderRow>
            <HeaderCell>번호</HeaderCell>
            <HeaderCell>제목</HeaderCell>
            <HeaderCell>작성자</HeaderCell>
            <HeaderCell>등록일</HeaderCell>
          </HeaderRow>

          <List>
            {pageItems.map((it) => {
              const indexForDisplay = it.isPublic
                ? ''
                : items.length - items.indexOf(it);
              return (
                <ListItem key={it.id}>
                  <ItemListItem
                    item={it}
                    order={order}
                    index={indexForDisplay}
                  />
                </ListItem>
              );
            })}
          </List>
        </>
      ) : (
        <PhotoGrid as="ul">
          {pageItems.map((it) => (
            <li key={it.id}>
              <ImageListItem item={it} order={order} />
            </li>
          ))}
        </PhotoGrid>
      )}

      <PaginationWrap>
        <PageNav cnt={pageCount} page={currentPage} change={setCurrentPage} />
      </PaginationWrap>
    </Wrapper>
  );
};

export default ItemList;

/* styles */
const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 767px) {
    padding: 0 5%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 8px;
  }
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const StateText = styled.h3`
  font-size: 20px;
  color: #333333;
  line-height: 33px;
  font-family: 'koPubWorld DotumB';
  margin: 0;
  flex: 1;

  @media (max-width: 767px) {
    display: none;
  }
`;

const HeaderRow = styled.ul`
  display: grid;
  grid-template-columns: 9% minmax(0, 1fr) 21% 21%;
  width: 100%;
  border-top: 1px solid #e5857c;
  border-bottom: 3px solid #e5857c;
  padding: 12px 0;
  list-style: none;
  margin: 20px 0 0;
  box-sizing: border-box;

  & > li {
    box-sizing: border-box;
    position: relative;
    padding: 0 5px; /* 본문과 padding 맞춤 */
    text-align: center;
    font-size: 20px;
    font-family: 'koPubWorld DotumM';
    color: #333333;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const HeaderCell = styled.li`
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 1px;
    background: #828282;
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
`;

const ListItem = styled.li`
  width: 100%;
`;

const PhotoGrid = styled.ul`
  margin-top: 5%;
  border-top: 2px solid #e5857c;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;

  > li {
    width: 50%;
    box-sizing: border-box;
    display: flex;
  }

  @media (min-width: 768px) {
    > li {
      width: 100%;
    }
  }
`;

const PaginationWrap = styled.div`
  width: 100%;
  margin: 20px 0;
`;
